import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { Carregando, ErroBox, PageHeader, Vazio } from '../components/ui';

/**
 * Os custos de IA da plataforma.
 *
 * ⚠️ **`ai_usage` era gravada desde a fase 3 e nenhuma rota a lia.** Dado
 * completo — tokens de entrada, saída, leitura e escrita de cache separados,
 * latência, finalidade, evento e participante — e consumidor nenhum. Até
 * 13/10/2026 a única forma de saber quanto a plataforma gastou era consultar o
 * banco por SSH.
 *
 * **O que esta tela NÃO faz:** não impõe teto, não alerta e não cobra. Ela
 * responde "quanto custou e onde". Um freio de gasto é outra rodada, e fingir
 * que esta tela é um freio seria pior que não ter nenhuma.
 */

interface Custos {
  periodo: { dias: number; desde: string };
  total: {
    chamadas: number;
    usd: number;
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
    latenciaMediaMs: number;
  };
  cache: { aproveitamento: number };
  semPreco: { chamadas: number; modelos: string[] };
  /** Geradas e cobradas, mas recusadas pelo formato ou cortadas no teto. Já estão no total. */
  perdidas: { chamadas: number; usd: number };
  porModelo: {
    modelo: string;
    chamadas: number;
    usd: number;
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
    semPreco: boolean;
  }[];
  porFinalidade: {
    finalidade: string;
    chamadas: number;
    usd: number;
    latenciaMediaMs: number;
  }[];
  porCliente: { organizationId: string; nome: string; chamadas: number; usd: number }[];
}

/**
 * Dólares, sempre na mesma forma.
 *
 * ⚠️ **A tela nasceu misturando duas notações, e o print do primeiro uso mostrou
 * o estrago:** o total grande saía `US$ 1,311` (pt-BR) e a mesma quantia, na
 * tabela por cliente, saía `US$ 1.3113` (`toFixed`, que é sempre en-US). Em
 * português o primeiro parece mil trezentos e onze e o segundo parece treze mil
 * — duas leituras erradas do mesmo número, na mesma tela.
 *
 * Quatro casas em todo lugar porque uma chamada custa centésimos de dólar: com
 * duas, quase toda linha vira `0,00`. E vírgula decimal em todas, porque quem lê
 * é brasileiro — o cifrão é que é de dólar, não a notação.
 */
function usd(valor: number): string {
  return `US$ ${valor.toLocaleString('pt-BR', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  })}`;
}

const JANELAS = [7, 30, 90, 365];

/**
 * O nome de cada finalidade para quem lê.
 *
 * `purpose` é `VarChar(60)` livre, então uma finalidade nova aparece com o
 * próprio código em vez de sumir — mesma regra dos `kind` de objetivo.
 */
const FINALIDADES: Record<string, string> = {
  jornada: 'Conduzir a conversa',
  interpretacao: 'Interpretar o material',
  classificacao: 'Triar mensagem',
  avaliacao: 'Sintetizar o perfil',
  matchmaking: 'Cruzar participantes',
  relatorio: 'Relatórios',
};

export function CustosDeIa() {
  const [dias, setDias] = useState(30);

  const consulta = useQuery({
    queryKey: ['master', 'custos-ia', dias],
    queryFn: () => api<Custos>(`/master/ai-costs?dias=${dias}`),
  });

  const d = consulta.data;

  return (
    <>
      <PageHeader
        titulo="Custos de IA"
        subtitulo="O que a plataforma gastou com modelos, entre todos os clientes."
        acoes={
          <div className="seletor-janela">
            {JANELAS.map((j) => (
              <button
                key={j}
                className={`btn btn-sm ${dias === j ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setDias(j)}
              >
                {j === 365 ? '1 ano' : `${j} dias`}
              </button>
            ))}
          </div>
        }
      />

      {consulta.isPending && <Carregando />}
      {consulta.isError && <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />}

      {d && d.total.chamadas === 0 && (
        <Vazio
          icone="ti-cpu"
          titulo="Nenhuma chamada de IA no período"
          descricao="Toda chamada é contabilizada no momento em que acontece. Sem chamadas, não há custo."
        />
      )}

      {d && d.total.chamadas > 0 && (
        <>
          {/*
            ⚠️ O aviso vem ANTES do total, e não depois.

            Modelo fora da tabela de preços grava custo NULO de propósito — preço
            inventado é pior que preço nenhum. Mas então o total abaixo está
            incompleto, e um número incompleto apresentado como total é pior que
            os dois. Aconteceu de verdade: `claude-sonnet-4-6` entrou como modelo
            principal sem estar na tabela, e o painel teria mostrado a plataforma
            rodando de graça.
          */}
          {d.semPreco.chamadas > 0 && (
            <div className="alert alert-danger" style={{ marginBottom: 16 }}>
              <i className="ti ti-alert-triangle" />
              <span>
                <strong>O total abaixo está incompleto.</strong> {d.semPreco.chamadas} chamada(s)
                rodaram em modelo sem preço cadastrado ({d.semPreco.modelos.join(', ')}) e entram
                com custo zero. O conserto é uma linha em <code>PRECOS</code>, em{' '}
                <code>uso-de-ia.service.ts</code>.
              </span>
            </div>
          )}

          {/*
            Tentativa perdida é dinheiro pago sem trabalho em troca: a resposta foi
            gerada inteira e recusada pelo formato, ou cortada no teto de tokens.
            Até 13/10/2026 ela nem entrava no total — o painel mostrava menos do que
            a fatura. Agora entra, e este aviso diz quanto do total ela é.
          */}
          {d.perdidas.chamadas > 0 && (
            <div className="alert alert-warning" style={{ marginBottom: 16 }}>
              <i className="ti ti-alert-circle" />
              <span>
                <strong>{d.perdidas.chamadas} tentativa(s) perdida(s)</strong>, {usd(d.perdidas.usd)}{' '}
                do total: respostas geradas e cobradas que voltaram fora do formato ou cortadas
                no teto de tokens. O motivo de cada uma está no log do serviço.
              </span>
            </div>
          )}

          <div className="grid grid-4 mb-6">
            <Numero
              valor={usd(d.total.usd)}
              rotulo="Custo no período"
              detalhe={`Desde ${new Date(d.periodo.desde).toLocaleDateString('pt-BR')}`}
              destaque
            />
            <Numero
              valor={d.total.chamadas.toLocaleString('pt-BR')}
              rotulo="Chamadas"
              detalhe={`${usd(d.total.chamadas ? d.total.usd / d.total.chamadas : 0)} em média`}
            />
            <Numero
              valor={`${d.cache.aproveitamento}%`}
              rotulo="Aproveitamento do cache"
              detalhe={
                d.cache.aproveitamento >= 50
                  ? 'O prefixo estável está funcionando'
                  : 'Baixo — algo volátil pode estar no prefixo'
              }
            />
            <Numero
              valor={`${(d.total.latenciaMediaMs / 1000).toFixed(1)}s`}
              rotulo="Latência média"
              detalhe="Por chamada"
            />
          </div>

          {/*
            O cache ganha cartão próprio porque é a decisão de custo mais
            importante da fase 3 e a mais fácil de quebrar sem perceber: leitura
            custa 10% da entrada e escrita 1,25x, e quem puser data, id ou nome no
            prefixo não recebe erro nenhum — só uma fatura maior.
          */}
          <div className="card mb-6">
            <div className="section-title">
              <i className="ti ti-database" /> De onde vieram os tokens de prompt
            </div>

            <div className="grid grid-3">
              <Token valor={d.total.inputTokens} rotulo="Entrada nova" detalhe="preço cheio" />
              <Token
                valor={d.total.cacheReadTokens}
                rotulo="Lidos do cache"
                detalhe="10% do preço"
              />
              <Token
                valor={d.total.cacheWriteTokens}
                rotulo="Escritos no cache"
                detalhe="1,25× o preço"
              />
            </div>

            <div className="nota-pe" style={{ marginTop: 12 }}>
              Saída: {d.total.outputTokens.toLocaleString('pt-BR')} tokens — é a parte cara, e
              nenhum cache a alcança.
            </div>
          </div>

          <div className="grid grid-2 mb-6">
            <Tabela
              titulo="Por modelo"
              icone="ti-cpu"
              colunas={['Modelo', 'Chamadas', 'Custo']}
              linhas={d.porModelo.map((m) => ({
                chave: m.modelo,
                celulas: [
                  <>
                    <span className="celula-principal">{m.modelo}</span>
                    {m.semPreco && (
                      <div className="celula-secundaria text-danger">sem preço cadastrado</div>
                    )}
                  </>,
                  m.chamadas.toLocaleString('pt-BR'),
                  m.semPreco ? '—' : usd(m.usd),
                ],
              }))}
            />

            <Tabela
              titulo="Por finalidade"
              icone="ti-target"
              colunas={['O que a IA fez', 'Chamadas', 'Custo']}
              linhas={d.porFinalidade.map((f) => ({
                chave: f.finalidade,
                celulas: [
                  <>
                    <span className="celula-principal">
                      {FINALIDADES[f.finalidade] ?? f.finalidade}
                    </span>
                    <div className="celula-secundaria">
                      {(f.latenciaMediaMs / 1000).toFixed(1)}s em média
                    </div>
                  </>,
                  f.chamadas.toLocaleString('pt-BR'),
                  usd(f.usd),
                ],
              }))}
            />
          </div>

          <Tabela
            titulo="Por cliente"
            icone="ti-building"
            colunas={['Cliente', 'Chamadas', 'Custo']}
            linhas={d.porCliente.map((c) => ({
              chave: c.organizationId,
              celulas: [
                <span className="celula-principal">{c.nome}</span>,
                c.chamadas.toLocaleString('pt-BR'),
                usd(c.usd),
              ],
            }))}
          />

          <aside className="consideracoes" style={{ marginTop: 16 }}>
            <div className="consideracoes-titulo">
              <i className="ti ti-info-circle" /> Sobre estes números
            </div>
            <p>
              São o custo <strong>medido</strong>, não estimado: cada linha é gravada no momento da
              chamada, com os tokens que a Anthropic devolveu. O preço vem de uma tabela no código,
              em dólares por milhão de tokens — se a Anthropic mudar o preço, o histórico já gravado
              não muda junto.
            </p>
            <p>
              Esta tela <strong>não impõe teto nem alerta</strong>. Uma jornada em laço ou uma
              importação grande aumentam a conta e nada aqui avisa.
            </p>
          </aside>
        </>
      )}
    </>
  );
}

function Numero({
  valor,
  rotulo,
  detalhe,
  destaque,
}: {
  valor: string;
  rotulo: string;
  detalhe: string;
  destaque?: boolean;
}) {
  return (
    <div className="stat-card">
      <div>
        <div className={`stat-value${destaque ? ' stat-accent' : ''}`}>{valor}</div>
        <div className="stat-label">{rotulo}</div>
      </div>
      <div className="text-dim" style={{ fontSize: 11 }}>
        {detalhe}
      </div>
    </div>
  );
}

function Token({ valor, rotulo, detalhe }: { valor: number; rotulo: string; detalhe: string }) {
  return (
    <div>
      <div className="stat-value" style={{ fontSize: 20 }}>
        {valor.toLocaleString('pt-BR')}
      </div>
      <div className="stat-label">{rotulo}</div>
      <div className="text-dim" style={{ fontSize: 11 }}>
        {detalhe}
      </div>
    </div>
  );
}

function Tabela({
  titulo,
  icone,
  colunas,
  linhas,
}: {
  titulo: string;
  icone: string;
  colunas: string[];
  linhas: { chave: string; celulas: React.ReactNode[] }[];
}) {
  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="section-title" style={{ padding: '14px 16px 0' }}>
        <i className={`ti ${icone}`} /> {titulo}
      </div>
      <div className="tabela-envolvente">
        <table className="data-table">
          <thead>
            <tr>
              {colunas.map((c) => (
                <th key={c}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.chave}>
                {l.celulas.map((c, i) => (
                  <td key={i}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
